import {
  Row,
  Col,
  Form,
  Fade,
  Button,
  Spinner,
  Collapse
} from "react-bootstrap";
import {
  useForm,
  useWatch,
  FormProvider,
  useFieldArray
} from "react-hook-form";
import { useSelector } from "react-redux";
import {
  faEdit,
  faPlus,
  faCheck,
  faXmark,
  faTrashAlt,
  faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useOutletContext, useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  SEND_DATA,
  PRICE_TYPE,
  DISBALE_LS_ACTIONS,
  LEAD_AGENT_PRICE_VARIANTS,
  LEAD_SERVICE_PLUS_EDIT_MODES
} from "../strings";
import {
  toggleLeadService,
  toggleLeadServicePlus,
  toggleAlternativeAddress,
  updateAlternativeAddress,
  syncNowOnDisableLeadservice,
  updateLeadServiceQuestionForAgent,
  updatePricePerInvoiceUnitForAgent,
  updateFixedPricePerInvoiceUnitForAgent,
  updateLeadServicePlusPriceTypeForAgent,
  getAfterConfirmationIntegrationIdsByFunnel
} from "../../services/api-declaration";
import CityForm from "../city/city-form";
import { FieldSet } from "../common-styling";
import { useDialog } from "../../hooks/useDialog";
import PreventReload from "../funnel-form/prevent-reload";
import TextField from "../reusable-form-fields/text-field";
import FormModal from "../create-new-modal/new-customer-modal";
import PriceTypeRadio from "./integration-view/price-type-radio";
import { showError, showSuccess } from "../../services/toast-service";
import NumberInputGroup from "../reusable-form-fields/number-input-group";
import { LeadServiceCompanyNameSchema } from "../../services/validation-schema";
import LeadServiceActionButton from "./integration-view/lead-service-action-button";
import {
  getLeadServicePlusFixedPriceVariants,
  getLeadServicePlusPriceVariants
} from "../lead-service-plus-price-variants";
import LeadServiceActionsSection from "./integration-view/lead-service-actions-section";
import ContactDetailsCommonFields from "../reusable-form-fields/contact-details-common-fields";
import { Trans, useTranslation } from "react-i18next";

const LeadService = ({
  toggleCb,
  emailIntegrations,
  zapierIntegrations,
  contactCloudIntegrations
}) => {
  const {
    FOR_COMPANY,
    FOR_PRODUCT,
    FOR_PRICE,
    FOR_FIXED_PRICE,
    FOR_UNIT,
    FOR_BUDGET_QUERY,
    FOR_PRICES_PER_AREA,
    FOR_BUDGET_QUERY_BOUNDS,
    FOR_INDIVIDUAL_NOTE
  } = LEAD_SERVICE_PLUS_EDIT_MODES;
  const { DYNAMIC, FIXED, AREA_QUERY } = PRICE_TYPE;
  const { STANDARD, FIXED_PRICE } = LEAD_AGENT_PRICE_VARIANTS;
  const { t } = useTranslation();
  const countryOptions = useSelector((state) => state.countryOptions.options);
  const {
    showConfirmationDialogBox,
    showDisableLeadServiceConfirmationDialogBox
  } = useDialog();
  const id = useParams().id;
  const { funnel, setFunnel } = useOutletContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingAlternativeAddress, setIsUpdatingAlternativeAddress] =
    useState(false);
  const [editMode, setEditMode] = useState({
    [FOR_COMPANY]: false,
    [FOR_PRODUCT]: false,
    [FOR_PRICE]: false,
    [FOR_FIXED_PRICE]: false,
    [FOR_UNIT]: false,
    [FOR_BUDGET_QUERY]: false,
    [FOR_PRICES_PER_AREA]: false,
    [FOR_BUDGET_QUERY_BOUNDS]: false,
    [FOR_INDIVIDUAL_NOTE]: false
  });
  const [createCityText, setCreateCityText] = useState("");
  const [allowAddNew, setAllowAddNew] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [isEditingPriceVariants, setIsEditingPriceVariants] = useState(false);
  const [isSubmittingPriceVariants, setIsSubmittingPriceVariants] =
    useState(false);
  const [initialPriceVariants, setInitialPriceVariants] = useState([]);
  const [isEditingFixedPriceVariants, setIsEditingFixedPriceVariants] =
    useState(false);
  const [isSubmittingFixedPriceVariants, setIsSubmittingFixedPriceVariants] =
    useState(false);
  const [initialFixedPriceVariants, setInitialFixedPriceVariants] = useState(
    []
  );
  const [isUpdatingPriceType, setIsUpdatingPriceType] = useState(false);
  const priceTypeAutosaveInFlightRef = useRef(false);
  const savedPriceTypeRef = useRef(funnel.leadServicePlusPriceType || DYNAMIC);

  const methods = useForm({
    resolver: yupResolver(LeadServiceCompanyNameSchema),
    defaultValues: {
      enabled: funnel.leadService,
      enabledPlus: funnel.leadServicePlus || false,
      companyNameForLeadAgent:
        funnel.companyNameForLeadAgent ||
        funnel.companyInfo?.companyName ||
        funnel.customer?.companyName ||
        "",
      leadServiceProductName:
        funnel.leadServiceProductName || funnel.category?.name || "",
      leadServicePlusPriceType: funnel.leadServicePlusPriceType || DYNAMIC,
      pricePerInvoiceUnitForLeadAgent:
        funnel.pricePerInvoiceUnitForLeadAgent || 0,
      [STANDARD]: getLeadServicePlusPriceVariants(funnel),
      fixedPricePerInvoiceUnitForLeadAgent:
        funnel.fixedPricePerInvoiceUnitForLeadAgent || 0,
      [FIXED_PRICE]: getLeadServicePlusFixedPriceVariants(funnel),
      nameOfAccountingUnitForLeadAgent:
        funnel.nameOfAccountingUnitForLeadAgent || funnel.category?.unit || "",
      alternativeAddressEnabled: funnel.alternativeAddress?.enabled || false,
      minBudgetQuery: funnel.minBudgetQuery || 0,
      maxBudgetQuery: funnel.maxBudgetQuery || 0,
      pricePerAreaBelowMinBudget: funnel.pricePerAreaBelowMinBudget || 0,
      pricePerAreaBetweenBudget: funnel.pricePerAreaBetweenBudget || 0,
      pricePerAreaAboveMaxBudget: funnel.pricePerAreaAboveMaxBudget || 0,
      leadServiceQuestions: funnel.leadServiceQuestions?.length
        ? funnel.leadServiceQuestions
        : [{ id: "question1", name: "" }],
      budgetAreaOfQueryBounds: funnel.budgetAreaOfQueryBounds ?? 20,
      leadServiceIndividualNote: funnel.leadServiceIndividualNote || ""
    }
  });
  const alternativeAddressCountry = funnel.alternativeAddress?.country
    ? funnel.alternativeAddress.country
    : funnel.customer.country;
  const methodsForAlternativeAddressFields = useForm({
    defaultValues: {
      country: countryOptions.some(
        (item) => item.name === alternativeAddressCountry
      )
        ? alternativeAddressCountry
        : "",
      city: funnel.alternativeAddress?.city || funnel.customer?.city || "",
      address:
        funnel.alternativeAddress?.address || funnel.customer?.address || "",
      zipCode:
        funnel.alternativeAddress?.zipCode ||
        String(funnel.customer?.zipCode) ||
        ""
    }
  });
  const {
    watch,
    control,
    setValue,
    register,
    getValues,
    reset,
    formState: { errors, dirtyFields }
  } = methods;

  useEffect(() => {
    savedPriceTypeRef.current = funnel.leadServicePlusPriceType || DYNAMIC;
  }, [funnel.leadServicePlusPriceType, DYNAMIC]);
  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion
  } = useFieldArray({
    control,
    name: "leadServiceQuestions"
  });
  const {
    fields: priceVariantFields,
    append: appendPriceVariant,
    remove: removePriceVariant
  } = useFieldArray({ control, name: STANDARD, keyName: "fieldId" });
  const {
    fields: fixedPriceVariantFields,
    append: appendFixedPriceVariant,
    remove: removeFixedPriceVariant
  } = useFieldArray({ control, name: FIXED_PRICE, keyName: "fieldId" });
  const lsQuestions = useWatch({
    control,
    name: "leadServiceQuestions"
  });
  const selectedPriceType = useWatch({
    control,
    name: "leadServicePlusPriceType"
  });
  const priceVariants = useWatch({ control, name: STANDARD });
  const fixedPriceVariants = useWatch({ control, name: FIXED_PRICE });
  const {
    handleSubmit,
    setValue: setAlternativeAddressFields,
    reset: resetAlternativeAddressFields,
    formState: { dirtyFields: dirtyFieldsForAlternativeAddress }
  } = methodsForAlternativeAddressFields;

  const checkZapierOccurrence = () =>
    zapierIntegrations.some(
      (item) => item.sourceData.meta.sendData === SEND_DATA.AFTER_CONFIRMATION
    );
  const checkSendDataTypeOccurrence = (_checked) => {
    const flags = { forEmail: false, forContactCloud: false, forZapier: false };
    const sendDataValue = _checked
      ? SEND_DATA.IMMEDIATELY
      : SEND_DATA.AFTER_CONFIRMATION;
    if (
      emailIntegrations.length &&
      emailIntegrations[0].sourceData.meta.sendData === sendDataValue
    ) {
      flags.forEmail = true;
    }
    if (
      contactCloudIntegrations.length &&
      contactCloudIntegrations[0].sourceData.meta.sendData === sendDataValue
    ) {
      flags.forContactCloud = true;
    }
    if (
      zapierIntegrations.length &&
      ((_checked && !checkZapierOccurrence()) ||
        (!_checked && checkZapierOccurrence()))
    ) {
      flags.forZapier = true;
    }
    return flags;
  };
  const handleToggleLeadService = async ({ payload, forPlus }) => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      let response = {};
      if (forPlus) {
        response = await toggleLeadServicePlus(payload, id);
      } else {
        response = await toggleLeadService(payload, id);
      }
      showSuccess(response.data.message);
    } catch (error) {
      console.log("error in handleToggleLeadService ", error);
      showError(t("leadServiceError"));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleLeadServiceToggle = async (_checked) => {
    let data = undefined;
    let associatedLeadsCount = 0;
    if (!_checked) {
      // check integration association in leads
      try {
        const response = await getAfterConfirmationIntegrationIdsByFunnel(id);
        data = response.data.data;
        associatedLeadsCount = data.leadsCount;
      } catch (error) {
        console.log("error in fetching integrations", error);
      }
    }
    if (associatedLeadsCount) {
      showDisableLeadServiceConfirmationDialogBox({
        title: t("disableLeadServiceModal.heading"),
        dialogType: "danger",
        dialogMessage: renderAssociatedLeadsDialogMessage(associatedLeadsCount),
        responseAction: (action, btnEventType) =>
          disableLeadserviceAndUpdateLeadsResponse({
            action,
            btnEventType,
            _checked,
            ids: data.integrationIds
          })
      });
    } else {
      toggleLeadServiceConfirmation(_checked);
    }
  };
  const renderAssociatedLeadsDialogMessage = (count) => (
    <Trans
      i18nKey={t("disableLeadServiceModal.associatedIntegrationMsg", { count })}
    />
  );
  const handleSyncNowAndDisable = async (integrationIds) => {
    try {
      await syncNowOnDisableLeadservice(integrationIds);
    } catch (error) {
      showError(error.response?.data?.message || "Error in sync leads");
    }
  };
  const disableLeadserviceAndUpdateLeadsResponse = async ({
    action,
    btnEventType,
    _checked,
    ids
  }) => {
    if (action && ids?.length) {
      switch (btnEventType) {
        case DISBALE_LS_ACTIONS.SYNC_NOW_AND_DISABLE:
          handleSyncNowAndDisable(ids);
          leadServiceDialogResponse(true, _checked);
          break;
        case DISBALE_LS_ACTIONS.DISABLE_NOW_AND_SYNC_LATER:
          leadServiceDialogResponse(true, _checked);
          break;
        default:
          break;
      }
    }
  };
  const handleLeadServicePlusToggle = (_checked) => {
    toggleLeadServicePlusConfirmation(_checked);
  };
  const toggleLeadServiceConfirmation = (_checked) => {
    showConfirmationDialogBox({
      title: "Warning",
      dialogType: "warning",
      dialogMessage: renderLeadServiceDialogMessage(_checked),
      responseAction: (action) => leadServiceDialogResponse(action, _checked)
    });
  };
  const toggleLeadServicePlusConfirmation = (_checked) => {
    showConfirmationDialogBox({
      title: "Warning",
      dialogType: "warning",
      dialogMessage: renderLeadServicePlusDialogMessage(_checked),
      responseAction: (action) =>
        leadServicePlusDialogResponse(action, _checked)
    });
  };
  const toggleDeleteQuestionConfirmation = ({ questionIndex }) => {
    showConfirmationDialogBox({
      title: t("common.confirmDeletion"),
      dialogType: "danger",
      dialogMessage: renderDeleteQuestionDialogMessage(),
      responseAction: (action) =>
        deleteQuestionDialogResponseAction({ action, questionIndex })
    });
  };
  const renderLeadServiceDialogMessage = (_checked) => {
    const { forEmail, forContactCloud, forZapier } =
      checkSendDataTypeOccurrence(_checked);
    const content = _checked
      ? t("funnelIntegration.leadService.enableDialogMsg")
      : forEmail || forContactCloud || forZapier
        ? t("funnelIntegration.leadService.warningDialogMessage")
        : t("funnelIntegration.leadService.disableDialogMsg");
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: content
        }}
      ></div>
    );
  };
  const renderLeadServicePlusDialogMessage = (_checked) => {
    const content = _checked
      ? t("funnelIntegration.leadService.enablePlusMsg")
      : t("funnelIntegration.leadService.disablePlusMsg");
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: content
        }}
      ></div>
    );
  };
  const renderDeleteQuestionDialogMessage = () => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: t("incompleteLeadDialog.msg")
        }}
      ></div>
    );
  };
  const updateFormAndFunnelValues = (property, _checked, updatePayload) => {
    setValue(property, _checked);
    setFunnel({ ...funnel, ...updatePayload });
  };
  const leadServiceDialogResponse = (action, _checked) => {
    if (action) {
      const { forEmail, forContactCloud, forZapier } =
        checkSendDataTypeOccurrence(_checked);
      const payload = { leadService: _checked };
      handleToggleLeadService({ payload, forPlus: false });
      if (forEmail || forContactCloud || forZapier) {
        toggleCb({ forEmail, forContactCloud, forZapier, checked: _checked });
      }
      updateFormAndFunnelValues("enabled", _checked, payload);
    }
  };
  const leadServicePlusDialogResponse = (action, _checked) => {
    if (action) {
      const { forEmail, forContactCloud, forZapier } =
        checkSendDataTypeOccurrence(_checked);
      const payload = {
        leadServicePlus: _checked,
        leadService: watch("enabled")
      };
      handleToggleLeadService({ payload: payload, forPlus: true });
      if (_checked) {
        setValue("enabled", _checked);
        if (forEmail || forContactCloud || forZapier) {
          toggleCb({ forEmail, forContactCloud, forZapier, checked: _checked });
        }
      }
      payload.leadService = watch("enabled");
      updateFormAndFunnelValues("enabledPlus", _checked, payload);
    }
  };
  const deleteQuestionDialogResponseAction = async ({
    action,
    questionIndex
  }) => {
    if (action) {
      try {
        const filteredQuestions = lsQuestions.filter(
          (_, index) => index !== questionIndex
        );
        await updateLeadServiceQuestionForAgent(id, {
          leadServiceQuestions: filteredQuestions
        });
        removeQuestion(questionIndex);
        setFunnel({ ...funnel, leadServiceQuestions: filteredQuestions });
        showSuccess("Question removed successfully");
      } catch (error) {
        console.log("error in deleting question ", error);
        showError("Error in deleting question");
      }
    }
  };
  const handleAlternativeAddress = async (_checked) => {
    try {
      if (isUpdatingAlternativeAddress) return;
      setIsUpdatingAlternativeAddress(true);
      const response = await toggleAlternativeAddress(
        { alternativeAddress: _checked },
        id
      );
      setValue("alternativeAddressEnabled", _checked);
      showSuccess(response.data.message);
      setFunnel({
        ...funnel,
        alternativeAddress: { ...funnel.alternativeAddress, enabled: _checked }
      });
    } catch (error) {
      console.log("error in handleAlternativeAddress ", error);
      showError(t("funnelIntegration.alternativeAddress.error"));
    } finally {
      setIsUpdatingAlternativeAddress(false);
    }
  };
  const submitAlternativeAddress = async (values) => {
    setIsUpdatingAlternativeAddress(true);
    const payload = {};
    Object.keys(dirtyFieldsForAlternativeAddress).forEach((key) => {
      payload[key] = values[key];
    });
    payload.enabled = watch("alternativeAddressEnabled");
    try {
      const response = await updateAlternativeAddress(payload, id);
      showSuccess(response.data.message);
      resetAlternativeAddressFields({
        country: values.country,
        city: values.city,
        address: values.address,
        zipCode: values.zipCode
      });
    } catch (error) {
      console.log("error in submitAlternativeAddress ", error);
      showError(t("funnelIntegration.alternativeAddress.error"));
    } finally {
      setIsUpdatingAlternativeAddress(false);
    }
  };
  const handleCreateCityText = (cityName) => setCreateCityText(cityName);
  const handleAllowAddNew = (flag) => setAllowAddNew(flag);
  const handleSaveQuestion = async () => {
    try {
      const allQuestions = getValues("leadServiceQuestions");
      const cleanedQuestions = allQuestions
        .map((q) => ({ id: q.id, name: q.name?.trim() }))
        .filter((q) => q.name); // removes empty questions
      await updateLeadServiceQuestionForAgent(id, {
        leadServiceQuestions: cleanedQuestions
      });
      showSuccess("Question updated successfully");
      setEditingQuestionId(null);
      // sync form state so UI stays clean
      setValue("leadServiceQuestions", cleanedQuestions, {
        shouldDirty: false
      });
      setFunnel({ ...funnel, leadServiceQuestions: cleanedQuestions });
    } catch (error) {
      showError("Error updating question");
    }
  };
  const handleCancelEditQuestion = ({ fieldVal, index }) => {
    setValue(`${`leadServiceQuestions.${index}.name`}`, fieldVal);
    setEditingQuestionId(null);
  };
  const handleDeleteQuestion = ({ index }) => {
    const question = questionFields[index];
    // not saved yet
    if (!question.name?.trim()) {
      removeQuestion(index);
      return;
    }
    // show confirmation dialog before deleting saved question
    toggleDeleteQuestionConfirmation({
      questionIndex: index
    });
  };
  const getPositiveNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : null;
  };

  const cleanPriceVariants = (variants = []) =>
    variants.map((variant, index) => ({
      id: variant.id || `variant-${index + 1}`,
      name: variant.name?.trim() || "",
      price: Number(variant.price || 0)
    }));

  const getPriceVariantValidation = (variants = []) => {
    const sourceVariants = variants || [];
    const cleanedVariants = cleanPriceVariants(sourceVariants);
    const requiresNames = cleanedVariants.length > 1;
    const names = cleanedVariants.map((variant) =>
      variant.name.trim().toLowerCase()
    );
    const hasMissingName = requiresNames && names.some((name) => !name);
    const hasDuplicateName =
      requiresNames && new Set(names).size !== names.length;
    const hasInvalidPrice = sourceVariants.some(
      (variant) => getPositiveNumber(variant.price) === null
    );

    return {
      isValid: !hasMissingName && !hasDuplicateName && !hasInvalidPrice,
      hasDuplicateName,
      hasMissingName,
      hasInvalidPrice
    };
  };

  const handleEditPriceVariants = () => {
    setInitialPriceVariants(cleanPriceVariants(getValues(STANDARD)));
    setIsEditingPriceVariants(true);
  };

  const handleCancelPriceVariants = () => {
    reset({ ...getValues(), [STANDARD]: initialPriceVariants });
    setIsEditingPriceVariants(false);
  };

  const handleAddPriceVariant = () => {
    if (!isEditingPriceVariants) {
      setInitialPriceVariants(cleanPriceVariants(getValues(STANDARD)));
      setIsEditingPriceVariants(true);
    }
    appendPriceVariant({
      id: `variant-${Date.now()}`,
      name: "",
      price: ""
    });
  };

  const handleRemovePriceVariant = (index) => {
    if (priceVariantFields.length <= 1) return;
    removePriceVariant(index);
  };

  const handleSavePriceVariants = async () => {
    const cleanedVariants = cleanPriceVariants(getValues(STANDARD));
    const { isValid } = getPriceVariantValidation(cleanedVariants);
    if (!isValid) return;

    try {
      setIsSubmittingPriceVariants(true);
      const primaryPrice = cleanedVariants[0]?.price || 0;
      const payload = {
        leadServicePlusPriceType: DYNAMIC,
        pricePerInvoiceUnitForLeadAgent: primaryPrice,
        [STANDARD]: cleanedVariants
      };
      const response = await updatePricePerInvoiceUnitForAgent(id, payload);
      showSuccess(response.data.message);
      setFunnel({
        ...funnel,
        ...payload
      });
      reset({
        ...getValues(),
        ...payload
      });
      setIsEditingPriceVariants(false);
    } catch (error) {
      showError(error.response?.data?.message || t("leadServiceError"));
    } finally {
      setIsSubmittingPriceVariants(false);
    }
  };

  const handleEditFixedPriceVariants = () => {
    setInitialFixedPriceVariants(cleanPriceVariants(getValues(FIXED_PRICE)));
    setIsEditingFixedPriceVariants(true);
  };

  const handleCancelFixedPriceVariants = () => {
    reset({ ...getValues(), [FIXED_PRICE]: initialFixedPriceVariants });
    setIsEditingFixedPriceVariants(false);
  };

  const handleAddFixedPriceVariant = () => {
    if (!isEditingFixedPriceVariants) {
      setInitialFixedPriceVariants(cleanPriceVariants(getValues(FIXED_PRICE)));
      setIsEditingFixedPriceVariants(true);
    }
    appendFixedPriceVariant({
      id: `variant-${Date.now()}`,
      name: "",
      price: ""
    });
  };

  const handleRemoveFixedPriceVariant = (index) => {
    if (fixedPriceVariantFields.length <= 1) return;
    removeFixedPriceVariant(index);
  };

  const handleSaveFixedPriceVariants = async () => {
    const cleanedVariants = cleanPriceVariants(getValues(FIXED_PRICE));
    const { isValid } = getPriceVariantValidation(cleanedVariants);
    if (!isValid) return;

    try {
      setIsSubmittingFixedPriceVariants(true);
      const primaryPrice = cleanedVariants[0]?.price || 0;
      const payload = {
        leadServicePlusPriceType: FIXED,
        fixedPricePerInvoiceUnitForLeadAgent: primaryPrice,
        [FIXED_PRICE]: cleanedVariants
      };
      const response = await updateFixedPricePerInvoiceUnitForAgent(
        id,
        payload
      );
      showSuccess(response.data.message);
      setFunnel({
        ...funnel,
        ...payload
      });
      reset({
        ...getValues(),
        ...payload
      });
      setIsEditingFixedPriceVariants(false);
    } catch (error) {
      showError(error.response?.data?.message || t("leadServiceError"));
    } finally {
      setIsSubmittingFixedPriceVariants(false);
    }
  };

  const isLastQuestionEmpty = useMemo(() => {
    if (!lsQuestions?.length) return false;
    const last = lsQuestions[lsQuestions.length - 1];
    return !last?.name?.trim();
  }, [lsQuestions]);
  const priceVariantValidation = getPriceVariantValidation(priceVariants);
  const fixedPriceVariantValidation =
    getPriceVariantValidation(fixedPriceVariants);
  const hasMultiplePriceVariants = priceVariantFields.length > 1;
  const hasMultipleFixedPriceVariants = fixedPriceVariantFields.length > 1;
  const getPrimaryDynamicPrice = () => {
    const variants = cleanPriceVariants(getValues(STANDARD));
    return (
      getPositiveNumber(variants[0]?.price) ||
      getPositiveNumber(getValues("pricePerInvoiceUnitForLeadAgent"))
    );
  };
  const getPrimaryFixedPrice = () => {
    const variants = cleanPriceVariants(getValues(FIXED_PRICE));
    return (
      getPositiveNumber(variants[0]?.price) ||
      getPositiveNumber(getValues("fixedPricePerInvoiceUnitForLeadAgent"))
    );
  };
  const hasValidDynamicPrice = () => getPrimaryDynamicPrice() !== null;
  const hasValidFixedPrice = () => getPrimaryFixedPrice() !== null;
  const hasValidAreaQueryPrice = () =>
    getPositiveNumber(getValues("minBudgetQuery")) !== null &&
    getPositiveNumber(getValues("maxBudgetQuery")) !== null &&
    getPositiveNumber(getValues("pricePerAreaBelowMinBudget")) !== null &&
    getPositiveNumber(getValues("pricePerAreaBetweenBudget")) !== null &&
    getPositiveNumber(getValues("pricePerAreaAboveMaxBudget")) !== null;

  const handlePriceTypeChange = async ({ target: { value } }) => {
    const savedPriceType = savedPriceTypeRef.current;
    setValue("leadServicePlusPriceType", value);
    const canAutosave =
      (value === DYNAMIC && hasValidDynamicPrice()) ||
      (value === FIXED && hasValidFixedPrice()) ||
      (value === AREA_QUERY && hasValidAreaQueryPrice());

    if (
      value === savedPriceType ||
      !canAutosave ||
      priceTypeAutosaveInFlightRef.current
    ) {
      return;
    }

    try {
      priceTypeAutosaveInFlightRef.current = true;
      setIsUpdatingPriceType(true);
      let response;
      if (value === DYNAMIC) {
        const cleanedVariants = cleanPriceVariants(getValues(STANDARD));
        response = await updatePricePerInvoiceUnitForAgent(id, {
          leadServicePlusPriceType: value,
          pricePerInvoiceUnitForLeadAgent: getPrimaryDynamicPrice(),
          [STANDARD]: cleanedVariants
        });
      } else if (value === FIXED) {
        const cleanedVariants = cleanPriceVariants(getValues(FIXED_PRICE));
        response = await updateFixedPricePerInvoiceUnitForAgent(id, {
          leadServicePlusPriceType: value,
          fixedPricePerInvoiceUnitForLeadAgent: getPrimaryFixedPrice(),
          [FIXED_PRICE]: cleanedVariants
        });
      } else if (value === AREA_QUERY) {
        response = await updateLeadServicePlusPriceTypeForAgent(id, {
          leadServicePlusPriceType: value,
          resetAreaQueries: false
        });
      }
      savedPriceTypeRef.current = value;
      setValue("leadServicePlusPriceType", value);
      setFunnel({ ...funnel, leadServicePlusPriceType: value });
      showSuccess(response.data.message);
    } catch (error) {
      setValue("leadServicePlusPriceType", savedPriceType);
      showError(error.response?.data?.message || t("leadServiceError"));
    } finally {
      priceTypeAutosaveInFlightRef.current = false;
      setIsUpdatingPriceType(false);
    }
  };

  return (
    <div className="px-3">
      <FieldSet>
        <FormProvider {...methods}>
          <PreventReload />
          <Form className="p-0">
            <Form.Check
              id="enabled"
              type="checkbox"
              label={t("common.enabled")}
              disabled={watch("enabledPlus")}
              checked={watch("enabled")}
              onChange={({ target: { checked } }) =>
                handleLeadServiceToggle(checked)
              }
            />
            <Form.Check
              id="enabledPlus"
              type="checkbox"
              label={`${t("common.enabled")} - Leadservice+`}
              checked={watch("enabledPlus")}
              onChange={({ target: { checked } }) =>
                handleLeadServicePlusToggle(checked)
              }
            />
            <Collapse in={watch("enabled") || watch("enabledPlus")}>
              <div>
                {/* leadServiceCompanyName field */}
                <Row className="align-items-center">
                  <Col sm={4} md={2}>
                    <Form.Label
                      className={`mb-0 text-break fw-bold ${
                        errors?.companyNameForLeadAgent ? "text-danger" : ""
                      }`}
                    >
                      {`${t(
                        "funnelIntegration.leadService.leadServiceCompanyNameLabel"
                      )}:`}
                    </Form.Label>
                  </Col>
                  <Col sm={6} md={3}>
                    <Form.Control
                      size="sm"
                      type="text"
                      className={`${
                        errors?.companyNameForLeadAgent ? "border-danger" : ""
                      }`}
                      placeholder={`${t("common.enter")} ${t(
                        "funnelIntegration.leadService.leadServiceCompanyNameLabel"
                      )}`}
                      disabled={!editMode[FOR_COMPANY]}
                      {...register("companyNameForLeadAgent")}
                    />
                  </Col>
                  <LeadServiceActionsSection
                    editMode={editMode}
                    actionsFor={FOR_COMPANY}
                    handleEditMode={setEditMode}
                    isBtnDisabled={!dirtyFields?.companyNameForLeadAgent}
                  />
                </Row>
                {/* leadServiceProductName field */}
                <Row className="align-items-center mt-2">
                  <Col sm={4} md={2}>
                    <Form.Label className="mb-0 text-break fw-bold">
                      {`${t(
                        "funnelIntegration.leadService.leadServiceProductNameLabel"
                      )}:`}
                    </Form.Label>
                  </Col>
                  <Col sm={6} md={3}>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder={`${t("common.enter")} ${t(
                        "funnelIntegration.leadService.leadServiceProductNameLabel"
                      )}`}
                      disabled={!editMode[FOR_PRODUCT]}
                      {...register("leadServiceProductName")}
                    />
                  </Col>
                  <LeadServiceActionsSection
                    editMode={editMode}
                    actionsFor={FOR_PRODUCT}
                    handleEditMode={setEditMode}
                    isBtnDisabled={!dirtyFields?.leadServiceProductName}
                  />
                </Row>
              </div>
            </Collapse>

            <Collapse in={watch("enabledPlus")}>
              <div>
                <Form.Group className="my-2 border-top border-bottom">
                  <Form.Label className="fw-bold mt-1">
                    {t("funnelIntegration.leadServicePlus.priceTypeLabel")}:
                  </Form.Label>
                  <PriceTypeRadio
                    value={DYNAMIC}
                    name="leadServicePlusPriceType"
                    label={t(
                      "funnelIntegration.leadServicePlus.calculationLabel"
                    )}
                    isDisabled={isUpdatingPriceType}
                    onChange={handlePriceTypeChange}
                  />
                  <Collapse in={selectedPriceType === DYNAMIC}>
                    <div>
                      <div className="ms-4 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="text-break mb-0 fw-bold">
                            {`${t(
                              "funnelIntegration.leadServicePlus.pricePerInvoiceUnitLabel"
                            )}:`}
                          </Form.Label>
                          <div className="d-flex align-items-center flex-nowrap ms-3">
                            {isEditingPriceVariants ? (
                              <>
                                <LeadServiceActionButton
                                  faIcon={faCheck}
                                  isDisabled={!priceVariantValidation.isValid}
                                  btnVariant="outline-success"
                                  tooltipMsg={t("common.save")}
                                  isLoading={isSubmittingPriceVariants}
                                  clickHandler={handleSavePriceVariants}
                                />
                                <LeadServiceActionButton
                                  btnStyles="ms-2"
                                  faIcon={faXmark}
                                  btnVariant="outline-danger"
                                  tooltipMsg={t("common.cancel")}
                                  clickHandler={handleCancelPriceVariants}
                                />
                              </>
                            ) : (
                              <LeadServiceActionButton
                                faIcon={faEdit}
                                btnVariant="outline-primary"
                                tooltipMsg={t("common.edit")}
                                clickHandler={handleEditPriceVariants}
                              />
                            )}
                            <LeadServiceActionButton
                              btnStyles="ms-2"
                              faIcon={faPlus}
                              tooltipMsg={t(
                                "funnelIntegration.leadServicePlus.addAnotherOption"
                              )}
                              clickHandler={handleAddPriceVariant}
                            />
                          </div>
                        </div>
                        {priceVariantFields.map((field, index) => (
                          <Row
                            key={field.fieldId}
                            className="mb-2 align-items-center"
                          >
                            {hasMultiplePriceVariants && (
                              <Col sm={5} md={3}>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder={t(
                                    "funnelIntegration.leadServicePlus.variantNamePlaceholder"
                                  )}
                                  disabled={!isEditingPriceVariants}
                                  {...register(`${STANDARD}.${index}.name`)}
                                />
                              </Col>
                            )}
                            <Col sm={6} md={3}>
                              <NumberInputGroup
                                inputSize="sm"
                                name={`${STANDARD}.${index}.price`}
                                isDisabled={!isEditingPriceVariants}
                              />
                            </Col>
                            <Col>
                              {isEditingPriceVariants &&
                                hasMultiplePriceVariants && (
                                  <LeadServiceActionButton
                                    faIcon={faTrashAlt}
                                    tooltipMsg={t("delete")}
                                    btnVariant="outline-danger"
                                    clickHandler={() =>
                                      handleRemovePriceVariant(index)
                                    }
                                  />
                                )}
                            </Col>
                          </Row>
                        ))}
                        {isEditingPriceVariants &&
                          !priceVariantValidation.isValid && (
                            <small className="d-block text-danger mt-2">
                              {priceVariantValidation.hasInvalidPrice
                                ? t(
                                    "funnelIntegration.leadServicePlus.invalidVariantPrice"
                                  )
                                : priceVariantValidation.hasMissingName
                                  ? t(
                                      "funnelIntegration.leadServicePlus.variantNameRequired"
                                    )
                                  : t(
                                      "funnelIntegration.leadServicePlus.uniqueVariantNameRequired"
                                    )}
                            </small>
                          )}
                      </div>
                    </div>
                  </Collapse>
                  <PriceTypeRadio
                    value={FIXED}
                    name="leadServicePlusPriceType"
                    label={t(
                      "funnelIntegration.leadServicePlus.fixedPriceLabel"
                    )}
                    isDisabled={isUpdatingPriceType}
                    onChange={handlePriceTypeChange}
                  />
                  <Collapse in={selectedPriceType === FIXED}>
                    <div>
                      <div className="ms-4 mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Form.Label className="text-break mb-0 fw-bold">
                            {`${t(
                              "funnelIntegration.leadServicePlus.fixedPricePerInvoiceUnitLabel"
                            )}:`}
                          </Form.Label>
                          <div className="d-flex align-items-center flex-nowrap ms-3">
                            {isEditingFixedPriceVariants ? (
                              <>
                                <LeadServiceActionButton
                                  faIcon={faCheck}
                                  isDisabled={
                                    !fixedPriceVariantValidation.isValid
                                  }
                                  btnVariant="outline-success"
                                  tooltipMsg={t("common.save")}
                                  isLoading={isSubmittingFixedPriceVariants}
                                  clickHandler={handleSaveFixedPriceVariants}
                                />
                                <LeadServiceActionButton
                                  btnStyles="ms-2"
                                  faIcon={faXmark}
                                  btnVariant="outline-danger"
                                  tooltipMsg={t("common.cancel")}
                                  clickHandler={handleCancelFixedPriceVariants}
                                />
                              </>
                            ) : (
                              <LeadServiceActionButton
                                faIcon={faEdit}
                                btnVariant="outline-primary"
                                tooltipMsg={t("common.edit")}
                                clickHandler={handleEditFixedPriceVariants}
                              />
                            )}
                            <LeadServiceActionButton
                              btnStyles="ms-2"
                              faIcon={faPlus}
                              tooltipMsg={t(
                                "funnelIntegration.leadServicePlus.addAnotherOption"
                              )}
                              clickHandler={handleAddFixedPriceVariant}
                            />
                          </div>
                        </div>
                        {fixedPriceVariantFields.map((field, index) => (
                          <Row
                            key={field.fieldId}
                            className="mb-2 align-items-center"
                          >
                            {hasMultipleFixedPriceVariants && (
                              <Col sm={5} md={3}>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder={t(
                                    "funnelIntegration.leadServicePlus.variantNamePlaceholder"
                                  )}
                                  disabled={!isEditingFixedPriceVariants}
                                  {...register(`${FIXED_PRICE}.${index}.name`)}
                                />
                              </Col>
                            )}
                            <Col sm={6} md={3}>
                              <NumberInputGroup
                                inputSize="sm"
                                name={`${FIXED_PRICE}.${index}.price`}
                                isDisabled={!isEditingFixedPriceVariants}
                              />
                            </Col>
                            <Col>
                              {isEditingFixedPriceVariants &&
                                hasMultipleFixedPriceVariants && (
                                  <LeadServiceActionButton
                                    faIcon={faTrashAlt}
                                    tooltipMsg={t("delete")}
                                    btnVariant="outline-danger"
                                    clickHandler={() =>
                                      handleRemoveFixedPriceVariant(index)
                                    }
                                  />
                                )}
                            </Col>
                          </Row>
                        ))}
                        {isEditingFixedPriceVariants &&
                          !fixedPriceVariantValidation.isValid && (
                            <small className="d-block text-danger mt-2">
                              {fixedPriceVariantValidation.hasInvalidPrice
                                ? t(
                                    "funnelIntegration.leadServicePlus.invalidVariantPrice"
                                  )
                                : fixedPriceVariantValidation.hasMissingName
                                  ? t(
                                      "funnelIntegration.leadServicePlus.variantNameRequired"
                                    )
                                  : t(
                                      "funnelIntegration.leadServicePlus.uniqueVariantNameRequired"
                                    )}
                            </small>
                          )}
                      </div>
                    </div>
                  </Collapse>
                  <div className="mb-2 d-flex align-items-center">
                    <PriceTypeRadio
                      value={AREA_QUERY}
                      name="leadServicePlusPriceType"
                      label={t(
                        "funnelIntegration.leadServicePlus.areaQueryLabel"
                      )}
                      isDisabled={
                        isUpdatingPriceType ||
                        watch("nameOfAccountingUnitForLeadAgent") === ""
                      }
                      onChange={handlePriceTypeChange}
                    />
                    <Fade in={watch("nameOfAccountingUnitForLeadAgent") === ""}>
                      <small className="ms-4 fw-bold text-primary">
                        <FontAwesomeIcon
                          className="me-1"
                          icon={faExclamationCircle}
                        />
                        {t(
                          "funnelIntegration.leadServicePlus.disabledBudgetLimitRadioMsg"
                        )}
                      </small>
                    </Fade>
                  </div>
                  <Collapse in={selectedPriceType === AREA_QUERY}>
                    <div>
                      <Row className="mb-2 align-items-center">
                        <Col sm={4} md={2}>
                          <Form.Label className="ms-4 text-break mb-0 fw-bold">
                            {`${t(
                              "funnelIntegration.leadServicePlus.budgetQueryLimitsLabel"
                            )}:`}
                          </Form.Label>
                        </Col>
                        <Col sm={6} md={2}>
                          <NumberInputGroup
                            inputSize="sm"
                            name="minBudgetQuery"
                            isDisabled={!editMode[FOR_BUDGET_QUERY]}
                            groupTextSymbol={watch(
                              "nameOfAccountingUnitForLeadAgent"
                            )}
                          />
                        </Col>
                        <Col sm={6} md={2}>
                          <NumberInputGroup
                            inputSize="sm"
                            name="maxBudgetQuery"
                            isDisabled={!editMode[FOR_BUDGET_QUERY]}
                            groupTextSymbol={watch(
                              "nameOfAccountingUnitForLeadAgent"
                            )}
                          />
                        </Col>
                        <Col sm={0} md={2}></Col>
                        <LeadServiceActionsSection
                          editMode={editMode}
                          actionsFor={FOR_BUDGET_QUERY}
                          handleEditMode={setEditMode}
                          hideDelete
                        />
                      </Row>
                      <Row className="mb-2 align-items-center">
                        <Col sm={4} md={2}>
                          <Form.Label className="ms-4 text-break mb-0 fw-bold">
                            {`${t(
                              "funnelIntegration.leadServicePlus.pricePerAreaLabel"
                            )}:`}
                          </Form.Label>
                        </Col>
                        <Col sm={6} md={2}>
                          <NumberInputGroup
                            inputSize="sm"
                            name="pricePerAreaBelowMinBudget"
                            isDisabled={!editMode[FOR_PRICES_PER_AREA]}
                          />
                        </Col>
                        <Col sm={6} md={2}>
                          <NumberInputGroup
                            inputSize="sm"
                            name="pricePerAreaBetweenBudget"
                            isDisabled={!editMode[FOR_PRICES_PER_AREA]}
                          />
                        </Col>
                        <Col sm={6} md={2}>
                          <NumberInputGroup
                            inputSize="sm"
                            name="pricePerAreaAboveMaxBudget"
                            isDisabled={!editMode[FOR_PRICES_PER_AREA]}
                          />
                        </Col>
                        <LeadServiceActionsSection
                          editMode={editMode}
                          actionsFor={FOR_PRICES_PER_AREA}
                          handleEditMode={setEditMode}
                          hideDelete
                        />
                      </Row>
                    </div>
                  </Collapse>
                </Form.Group>
                <Row className="align-items-center my-3">
                  <Col sm={4} md={2}>
                    <Form.Label className="text-break mb-0 fw-bold">
                      {`${t(
                        "funnelIntegration.leadServicePlus.nameOfAccountingUnitLabel"
                      )}:`}
                    </Form.Label>
                  </Col>
                  <Col sm={6} md={3}>
                    <TextField
                      type="text"
                      inputSize="sm"
                      register={register}
                      disabled={!editMode[FOR_UNIT]}
                      name="nameOfAccountingUnitForLeadAgent"
                      noSpacing
                    />
                  </Col>
                  <LeadServiceActionsSection
                    editMode={editMode}
                    actionsFor={FOR_UNIT}
                    handleEditMode={setEditMode}
                    isBtnDisabled={
                      !dirtyFields?.nameOfAccountingUnitForLeadAgent
                    }
                  />
                </Row>
                <Row className="align-items-center my-3">
                  <Col sm={4} md={2}>
                    <Form.Label className="text-break mb-0 fw-bold">
                      {`${t(
                        "funnelIntegration.leadServicePlus.budgetAreaOfQueryBounds"
                      )}:`}
                    </Form.Label>
                  </Col>
                  <Col sm={6} md={1}>
                    <NumberInputGroup
                      inputSize="sm"
                      groupTextSymbol="%"
                      name="budgetAreaOfQueryBounds"
                      isDisabled={!editMode[FOR_BUDGET_QUERY_BOUNDS]}
                    />
                  </Col>
                  <LeadServiceActionsSection
                    editMode={editMode}
                    actionsFor={FOR_BUDGET_QUERY_BOUNDS}
                    handleEditMode={setEditMode}
                    isBtnDisabled={!dirtyFields?.budgetAreaOfQueryBounds}
                    hideDelete
                  />
                </Row>
              </div>
            </Collapse>

            <Collapse in={watch("enabled") || watch("enabledPlus")}>
              <div>
                <div className="mt-2 mb-4 border-top">
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <Form.Label className="text-break fw-bold mb-0">
                      {t("funnelIntegration.leadServicePlus.optionalQuestion")}:
                    </Form.Label>

                    <Button
                      size="sm"
                      variant="primary"
                      disabled={
                        editingQuestionId ||
                        questionFields.length >= 10 ||
                        isLastQuestionEmpty
                      }
                      onClick={() =>
                        appendQuestion({
                          id: `question${questionFields.length + 1}`,
                          name: ""
                        })
                      }
                    >
                      <FontAwesomeIcon className="me-1" icon={faPlus} />
                      {t("customerWebsite.addQ")}
                    </Button>
                  </div>

                  {questionFields.map((field, index) => (
                    <Row key={field.id} className="align-items-center mt-3">
                      <Col sm={4} md={2}>
                        <Form.Label className="text-break mb-0 ps-3">
                          {`${t(
                            "funnelIntegration.leadServicePlus.question"
                          )}: ${index + 1}`}
                        </Form.Label>
                      </Col>

                      <Col sm={6} md={4}>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Enter question"
                          disabled={editingQuestionId !== field.id}
                          {...register(`leadServiceQuestions.${index}.name`)}
                        />
                      </Col>

                      <Col sm="auto" className="d-flex align-items-center">
                        <Collapse
                          dimension="width"
                          in={editingQuestionId !== field.id}
                        >
                          <div>
                            <div className="d-flex align-items-center">
                              <LeadServiceActionButton
                                faIcon={faEdit}
                                btnVariant="outline-primary"
                                tooltipMsg={t("common.edit")}
                                clickHandler={() =>
                                  setEditingQuestionId(field.id)
                                }
                                isDisabled={editingQuestionId}
                              />
                              <LeadServiceActionButton
                                btnStyles="ms-2"
                                faIcon={faTrashAlt}
                                btnVariant="outline-danger"
                                tooltipMsg={t("delete")}
                                clickHandler={() => {
                                  handleDeleteQuestion({ index });
                                }}
                                isDisabled={editingQuestionId}
                              />
                            </div>
                          </div>
                        </Collapse>
                        <Collapse
                          dimension="width"
                          in={editingQuestionId === field.id}
                        >
                          <div>
                            <div className="d-flex align-items-center">
                              <LeadServiceActionButton
                                faIcon={faCheck}
                                btnVariant="outline-success"
                                tooltipMsg={t("common.save")}
                                clickHandler={handleSaveQuestion}
                                isDisabled={!lsQuestions?.[index]?.name?.trim()}
                              />
                              <LeadServiceActionButton
                                btnStyles="ms-2"
                                faIcon={faXmark}
                                btnVariant="outline-danger"
                                tooltipMsg={t("common.cancel")}
                                clickHandler={() =>
                                  handleCancelEditQuestion({
                                    fieldVal: field.name,
                                    index
                                  })
                                }
                              />
                            </div>
                          </div>
                        </Collapse>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            </Collapse>

            {/* alternative address fields */}
            <FormProvider {...methodsForAlternativeAddressFields}>
              <PreventReload />
              <div>
                <div
                  className={`d-flex align-items-center ${
                    watch("enabled") || watch("enabledPlus")
                      ? "mt-2 pt-2 border-top"
                      : ""
                  }`}
                >
                  <Form.Check
                    id="alternativeAddressEnabled"
                    type="checkbox"
                    checked={watch("alternativeAddressEnabled")}
                    label={t(
                      "funnelIntegration.alternativeAddress.checkboxLabel"
                    )}
                    onChange={({ target: { checked } }) =>
                      handleAlternativeAddress(checked)
                    }
                    disabled={isUpdatingAlternativeAddress}
                  />
                  {isUpdatingAlternativeAddress && (
                    <Spinner
                      size="sm"
                      animation="border"
                      className="ms-3 mb-1"
                    />
                  )}
                </div>
                <Collapse in={watch("alternativeAddressEnabled")}>
                  <div>
                    <div className="mt-1 p-3 bg-light rounded">
                      <ContactDetailsCommonFields
                        labelStyle="fw-bold"
                        handleAllowAddNew={handleAllowAddNew}
                        handleCreateCityText={handleCreateCityText}
                        countryValueField="name"
                      />
                      <Button
                        type="submit"
                        variant="success"
                        disabled={
                          isUpdatingAlternativeAddress ||
                          !Object.keys(dirtyFieldsForAlternativeAddress)?.length
                        }
                        onClick={(ev) => {
                          ev.preventDefault();
                          handleSubmit(submitAlternativeAddress)();
                        }}
                      >
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                </Collapse>
              </div>
            </FormProvider>

            <Collapse in={watch("enabled")}>
              <div>
                <div className="mt-2 pt-2 border-top">
                  <Row className="align-items-center">
                    <Col sm={4} md={2}>
                      <Form.Label className="mb-0 text-break fw-bold">
                        {`${t(
                          "funnelIntegration.leadService.individualNoteLabel"
                        )}:`}
                      </Form.Label>
                    </Col>
                    <Col sm={6} md={3}>
                      <Form.Control
                        size="sm"
                        as="textarea"
                        placeholder={`${t("common.enter")} ${t(
                          "funnelIntegration.leadService.individualNoteLabel"
                        )}`}
                        disabled={!editMode[FOR_INDIVIDUAL_NOTE]}
                        {...register("leadServiceIndividualNote")}
                      />
                    </Col>
                    <LeadServiceActionsSection
                      editMode={editMode}
                      actionsFor={FOR_INDIVIDUAL_NOTE}
                      handleEditMode={setEditMode}
                      isBtnDisabled={!dirtyFields?.leadServiceIndividualNote}
                    />
                  </Row>
                </div>
              </div>
            </Collapse>
          </Form>
        </FormProvider>
      </FieldSet>
      <FormModal
        show={allowAddNew}
        onHide={() => handleAllowAddNew(false)}
        modalHeading={t("createNewCity")}
        modalSize="md"
      >
        <CityForm
          createCityText={createCityText}
          handleAllowAddNew={handleAllowAddNew}
          setValue={setAlternativeAddressFields}
        />
      </FormModal>
    </div>
  );
};

export default LeadService;
